// Adds keyboard + screen-reader friendly navigation to a
// (v-menu + v-text-field + v-date-picker) combo, WITHOUT changing any visible
// text, styling, form validation, or the disableOutOfRangeDates business rules.
//
// Behaviour added:
//   - When the menu opens, focus moves into the calendar onto the selected date
//     (or the nearest selectable date if none is chosen yet).
//   - Arrow keys move focus to the previous/next SELECTABLE date. Left/Right step
//     by a day, Up/Down by a week, then snap to the nearest allowed date in that
//     direction. Disabled days (holidays / non-working / out-of-range) are skipped
//     automatically because navigation is driven by the component's own
//     disableOutOfRangeDates(). The displayed month follows the focused date.
//   - Enter / Space select the focused day (native <button> behaviour) and close.
//   - Escape closes the menu and returns focus to the text field.
//   - Closing the menu (for any reason) returns focus to the text field.
//
// Contract — a component using this mixin MUST provide:
//   data:   dpMenu                     (Boolean) v-model of the wrapping <v-menu>
//   method: disableOutOfRangeDates(s)  returns true if 'YYYY-MM-DD' is selectable
//   method: dpGetValue()               returns the selected 'YYYY-MM-DD' (or falsy)
//   refs:   datePicker                 ref on the <v-date-picker>
//           dateField                  ref on the activator <v-text-field>
// Template wiring on <v-date-picker>:
//   ref="datePicker" :picker-date.sync="dpPickerDate" @keydown.native="onDatePickerKeydown"
// Template wiring on <v-text-field>:
//   ref="dateField"

// Used https://ibm.github.io/vue-a11y-calendar/components/datepicker/ as inspiration for this

const MAX_SCAN_DAYS = 420; // safety cap when scanning for the next selectable date

export default {
  data() {
    return {
      dpPickerDate: null,     // 'YYYY-MM' (date view) / 'YYYY' (month view) shown by the picker
      dpActivePicker: 'DATE', // which sub-view is showing: DATE | MONTH | YEAR
      dpFocusedDate: null,    // 'YYYY-MM-DD' day button currently holding focus
      dpReturnFocus: false    // whether to hand focus back to the field on close
    };
  },
  watch: {
    dpMenu(isOpen) {
      if (isOpen) {
        this.dpReturnFocus = true;
        this.$nextTick(() => {
          this.dpFocusInitial();
          this.dpLabelDaysWhenReady(0);
        });
      } else if (this.dpReturnFocus) {
        this.dpReturnFocus = false;
        this.$nextTick(() => {
          const field = this.$refs.dateField;
          if (field && typeof field.focus === 'function') field.focus();
        });
      }
    },
    // Re-label day buttons whenever the displayed month changes (arrow keys, the
    // header arrows, or mouse) — picker-date.sync keeps dpPickerDate current.
    dpPickerDate() {
      if (this.dpMenu) this.dpLabelDaysWhenReady(0);
    },
    // React to switching between the day grid and the month grid.
    dpActivePicker(view) {
      if (!this.dpMenu) return;
      if (view === 'MONTH') {
        this.dpFocusActiveMonthWhenReady(0);
      } else if (view === 'DATE') {
        // back to the day grid (e.g. after choosing a month) — focus a day
        this.dpFocusedDate = null;
        this.dpFocusFirstDayWhenReady(0);
      }
    }
  },
  mounted() {
    // Off-screen polite live region used to confirm a date selection to
    // screen-reader users (one per component instance).
    const el = document.createElement('div');
    el.className = 'sr-only';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
    this.dpLiveRegion = el;
  },
  beforeDestroy() {
    if (this.dpLiveRegion && this.dpLiveRegion.parentNode) {
      this.dpLiveRegion.parentNode.removeChild(this.dpLiveRegion);
    }
    this.dpLiveRegion = null;
  },
  methods: {
    // ---- date-string helpers (local time, no timezone surprises) ----
    dpPad(n) {
      return String(n).padStart(2, '0');
    },
    dpParse(s) {
      const p = s.split('-');
      return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    },
    dpFormat(dt) {
      return dt.getFullYear() + '-' + this.dpPad(dt.getMonth() + 1) + '-' + this.dpPad(dt.getDate());
    },
    dpIsAllowed(s) {
      try {
        return this.disableOutOfRangeDates(s) === true;
      } catch (e) {
        return false;
      }
    },
    // nearest selectable date from `startStr` (inclusive), stepping by `step` days
    dpNearestAllowed(startStr, step) {
      const dt = this.dpParse(startStr);
      for (let i = 0; i < MAX_SCAN_DAYS; i++) {
        const s = this.dpFormat(dt);
        if (this.dpIsAllowed(s)) return s;
        dt.setDate(dt.getDate() + step);
      }
      return null;
    },

    // ---- DOM helpers ----
    dpRoot() {
      const picker = this.$refs.datePicker;
      return picker && picker.$el ? picker.$el : null;
    },
    dpTableButtons(root, selector) {
      // Exclude buttons in a table that is transitioning OUT, so focus never
      // lands on a disappearing month/year table.
      const leaving = root.querySelectorAll(
        '.tab-transition-leave-active, .tab-reverse-transition-leave-active'
      );
      const inLeaving = (el) => {
        for (let i = 0; i < leaving.length; i++) {
          if (leaving[i].contains(el)) return true;
        }
        return false;
      };
      const all = root.querySelectorAll(selector);
      const out = [];
      for (let i = 0; i < all.length; i++) {
        if (!inLeaving(all[i])) out.push(all[i]);
      }
      return out;
    },
    dpDayButtons(root) {
      return this.dpTableButtons(root, '.v-date-picker-table--date tbody button');
    },
    dpMonthButtons(root) {
      return this.dpTableButtons(root, '.v-date-picker-table--month tbody button');
    },
    dpGridColumns(root, selector) {
      const firstRow = root.querySelector(selector + ' tbody tr');
      const n = firstRow ? firstRow.querySelectorAll('button').length : 0;
      return n > 0 ? n : 3;
    },
    dpFocusDay(dateStr) {
      const root = this.dpRoot();
      if (!root) return false;
      const buttons = this.dpDayButtons(root);
      return this.dpFocusDayFromButtons(buttons, dateStr);
    },
    dpFocusDayFromButtons(buttons, dateStr) {
      if (!buttons.length) return false;
      // Apply labels + application role + roving tabindex BEFORE moving focus, so
      // the day is announced as a date (table already in focus mode) and Tab only
      // ever stops on the active day instead of cycling every cell.
      this.dpLabelDays(buttons);
      this.dpSetRovingTabindex(buttons, dateStr);
      const day = Number(dateStr.split('-')[2]);
      for (let i = 0; i < buttons.length; i++) {
        const b = buttons[i];
        if (!b.disabled && Number((b.textContent || '').trim()) === day) {
          b.focus();
          // Confirm focus actually landed. During the menu's open/scale transition
          // the button can exist but not yet be focusable; report failure so the
          // caller retries on the next frame.
          if (document.activeElement === b) {
            this.dpFocusedDate = dateStr;
            return true;
          }
          return false;
        }
      }
      return false;
    },
    // Roving tabindex: only the active day stays in the tab order, so Tab leaves
    // the calendar in one step instead of cycling through every day (which also
    // desynced the arrow-key navigation).
    dpSetRovingTabindex(buttons, dateStr) {
      const day = Number(dateStr.split('-')[2]);
      for (let i = 0; i < buttons.length; i++) {
        const b = buttons[i];
        const isTarget = !b.disabled && Number((b.textContent || '').trim()) === day;
        b.setAttribute('tabindex', isTarget ? '0' : '-1');
      }
    },

    // ---- focus management ----
    dpFocusInitial() {
      const selected = this.dpGetValue ? this.dpGetValue() : null;
      let target = (selected && this.dpIsAllowed(selected)) ? selected : null;
      if (!target) {
        const today = this.dpFormat(new Date());
        target = this.dpNearestAllowed(today, 1) || this.dpNearestAllowed(today, -1);
      }
      if (!target) return; // nothing selectable at all — leave focus as-is
      this.dpPickerDate = target.slice(0, 7);
      this.dpFocusDayWhenReady(target, 0);
    },

    // Wait (across animation frames) for the target day button to exist AND be
    // focusable, then focus it. Covers the menu's open/scale transition and the
    // table re-render that happens when the displayed month changes.
    dpFocusDayWhenReady(dateStr, attempt) {
      if (!this.dpMenu) return; // menu closed before it became ready
      if (this.dpFocusDay(dateStr)) return;
      if (attempt >= 60) return; // ~1s cap; give up rather than loop forever
      window.requestAnimationFrame(() => this.dpFocusDayWhenReady(dateStr, attempt + 1));
    },

    // ---- screen-reader labels for day buttons ----
    // Vuetify's day buttons contain only the day number (no aria-label), so a
    // screen reader announces table coordinates and a bare number. Give each day
    // a full, human-readable date label; re-applied whenever the month re-renders.
    dpLabelDaysWhenReady(attempt) {
      if (!this.dpMenu) return;
      const root = this.dpRoot();
      const buttons = root ? this.dpDayButtons(root) : [];
      if (buttons.length) {
        this.dpLabelDays(buttons);
        return;
      }
      if (attempt >= 60) return;
      window.requestAnimationFrame(() => this.dpLabelDaysWhenReady(attempt + 1));
    },
    dpLabelDays(buttons) {
      const month = this.dpPickerDate;
      if (!month || month.length < 7) return;
      const parts = month.split('-');
      const year = Number(parts[0]);
      const monthIndex = Number(parts[1]) - 1;

      // Mark the calendar table as an application region. Under a screen reader
      // this switches it to focus mode, so arrow keys reach our keydown handler
      // instead of driving the SR's virtual cursor through the text (which read
      // "out of button" and spelled out letters). It also drops the "row N" /
      // weekday-column ("T") table chatter. Day buttons stay focusable + labelled.
      const root = this.dpRoot();
      if (root) {
        const gridLabel = new Date(year, monthIndex, 1).toLocaleDateString(undefined, {
          month: 'long', year: 'numeric'
        });
        const tables = root.querySelectorAll('.v-date-picker-table--date table');
        for (let t = 0; t < tables.length; t++) {
          tables[t].setAttribute('role', 'application');
          tables[t].setAttribute('aria-label', gridLabel);
        }
        // The header (‹ › prev/next-month + month/year title) also needs focus
        // mode, or a screen reader reads "out of button" when arrowing over it.
        const header = root.querySelector('.v-date-picker-header');
        if (header) header.setAttribute('role', 'application');
      }
      for (let i = 0; i < buttons.length; i++) {
        const b = buttons[i];
        const day = Number((b.textContent || '').trim());
        if (!day) continue;
        let label = new Date(year, monthIndex, day).toLocaleDateString(undefined, {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
        if (b.classList.contains('v-btn--active')) label += ', selected';
        b.setAttribute('aria-label', label);
        if (b.classList.contains('v-date-picker-table__current')) {
          b.setAttribute('aria-current', 'date');
        } else {
          b.removeAttribute('aria-current');
        }
      }
    },

    // Centralized opener for readonly activators and appended calendar buttons.
    dpOpenMenu(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.dpMenu = true;
    },

    // ---- announce the selected date to screen readers ----
    // Wired to the date picker's @input, so it fires when a day is chosen.
    dpOnSelect(value) {
      this.dpMenu = false; // close the menu (replaces the previous inline @input)
      this.dpAnnounceSelection(value);
    },
    dpAnnounceSelection(value) {
      if (!this.dpLiveRegion) return;
      const label = (this.dpFieldLabel && this.dpFieldLabel()) || 'Date';
      let dateText = value || '';
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const p = value.split('-');
        dateText = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])).toLocaleDateString(undefined, {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
      }
      const message = label + ', ' + dateText + ', selected';
      const region = this.dpLiveRegion;
      // Clear first so re-selecting the same date still registers as a change.
      region.textContent = '';
      window.requestAnimationFrame(() => {
        if (this.dpLiveRegion === region) region.textContent = message;
      });
    },

    // ---- month sub-view (opened by clicking the month/year title) ----
    dpSetRovingGeneric(buttons, targetBtn) {
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('tabindex', buttons[i] === targetBtn ? '0' : '-1');
      }
    },
    dpLabelMonths(buttons) {
      const root = this.dpRoot();
      const year = parseInt(this.dpPickerDate, 10); // 'YYYY' (month view) or 'YYYY-MM'
      if (root) {
        const tables = root.querySelectorAll('.v-date-picker-table--month table');
        for (let t = 0; t < tables.length; t++) {
          tables[t].setAttribute('role', 'application');
          tables[t].setAttribute('aria-label', isNaN(year) ? 'Choose a month' : ('Choose a month, ' + year));
        }
        const header = root.querySelector('.v-date-picker-header');
        if (header) header.setAttribute('role', 'application');
      }
      // Month buttons render January..December in order, so index i is month i.
      for (let i = 0; i < buttons.length; i++) {
        const b = buttons[i];
        let label = new Date(isNaN(year) ? 2000 : year, i, 1).toLocaleDateString(undefined, {
          month: 'long', year: 'numeric'
        });
        if (b.classList.contains('v-btn--active')) label += ', selected';
        b.setAttribute('aria-label', label);
      }
    },
    dpFocusActiveMonthWhenReady(attempt) {
      if (!this.dpMenu || this.dpActivePicker !== 'MONTH') return;
      const root = this.dpRoot();
      const buttons = root ? this.dpMonthButtons(root) : [];
      if (buttons.length) {
        this.dpLabelMonths(buttons);
        let target = null;
        for (let i = 0; i < buttons.length; i++) {
          if (buttons[i].classList.contains('v-btn--active') && !buttons[i].disabled) { target = buttons[i]; break; }
        }
        if (!target) {
          for (let i = 0; i < buttons.length; i++) {
            if (!buttons[i].disabled) { target = buttons[i]; break; }
          }
        }
        if (target) {
          this.dpSetRovingGeneric(buttons, target);
          target.focus();
          if (document.activeElement === target) return;
        }
      }
      if (attempt >= 60) return;
      window.requestAnimationFrame(() => this.dpFocusActiveMonthWhenReady(attempt + 1));
    },
    dpNavigateMonths(root, key) {
      const buttons = this.dpMonthButtons(root);
      if (!buttons.length) return;
      this.dpLabelMonths(buttons);
      let idx = buttons.indexOf(document.activeElement);
      if (idx === -1) {
        for (let i = 0; i < buttons.length; i++) {
          if (buttons[i].classList.contains('v-btn--active')) { idx = i; break; }
        }
      }
      if (idx === -1) idx = 0;
      const cols = this.dpGridColumns(root, '.v-date-picker-table--month');
      let delta = 0;
      if (key === 'ArrowLeft') delta = -1;
      else if (key === 'ArrowRight') delta = 1;
      else if (key === 'ArrowUp') delta = -cols;
      else if (key === 'ArrowDown') delta = cols;
      else return;
      const step = delta < 0 ? -1 : 1;
      let target = idx + delta;
      while (target >= 0 && target < buttons.length && buttons[target].disabled) target += step;
      if (target < 0 || target >= buttons.length) return; // edge — stay put
      this.dpSetRovingGeneric(buttons, buttons[target]);
      buttons[target].focus();
    },
    dpFocusFirstDayWhenReady(attempt) {
      if (!this.dpMenu || this.dpActivePicker !== 'DATE') return;
      const root = this.dpRoot();
      const buttons = root ? this.dpDayButtons(root) : [];
      let target = null;
      for (let i = 0; i < buttons.length; i++) {
        if (!buttons[i].disabled) { target = buttons[i]; break; }
      }
      if (target && this.dpPickerDate && this.dpPickerDate.length >= 7) {
        this.dpLabelDays(buttons);
        const day = Number((target.textContent || '').trim());
        const dateStr = this.dpPickerDate.slice(0, 7) + '-' + this.dpPad(day);
        this.dpSetRovingTabindex(buttons, dateStr);
        target.focus();
        if (document.activeElement === target) { this.dpFocusedDate = dateStr; return; }
      }
      if (attempt >= 60) return;
      window.requestAnimationFrame(() => this.dpFocusFirstDayWhenReady(attempt + 1));
    },

    // ---- keyboard handler on the date picker ----
    onDatePickerKeydown(e) {
      const key = e.key;
      if (key === 'Escape' || key === 'Esc') {
        e.preventDefault();
        this.dpMenu = false; // watch() returns focus to the field
        return;
      }
      const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (!Object.prototype.hasOwnProperty.call(steps, key)) return; // let Tab/Enter/Space behave natively

      const root = this.dpRoot();
      if (!root) return;
      // Year list is left to the browser/screen reader (not custom-handled).
      if (this.dpActivePicker !== 'DATE' && this.dpActivePicker !== 'MONTH') return;

      const active = document.activeElement;

      if (this.dpActivePicker === 'MONTH') {
        e.preventDefault(); // we own arrow keys in the month grid
        // Only move when focus is on a month cell — not the header ‹ › buttons.
        if (this.dpMonthButtons(root).indexOf(active) !== -1) this.dpNavigateMonths(root, key);
        return;
      }

      // DATE view
      e.preventDefault(); // we own arrow keys in the day grid
      const dayButtons = this.dpDayButtons(root);
      // Focus on the header ‹ › / month-year title — leave the day selection put.
      if (dayButtons.indexOf(active) === -1) return;
      if (!this.dpFocusedDate) {
        // Adopt the focused day (e.g. the user Tabbed onto it) so the arrow moves from there.
        if (!active.disabled && this.dpPickerDate) {
          const d = Number((active.textContent || '').trim());
          if (d) this.dpFocusedDate = this.dpPickerDate.slice(0, 7) + '-' + this.dpPad(d);
        }
        if (!this.dpFocusedDate) {
          this.dpFocusInitial();
          return;
        }
      }
      const delta = steps[key];
      const dir = delta < 0 ? -1 : 1;
      const cand = this.dpParse(this.dpFocusedDate);
      cand.setDate(cand.getDate() + delta);
      const next = this.dpNearestAllowed(this.dpFormat(cand), dir);
      if (!next) return; // nothing selectable that way — stay put

      const month = next.slice(0, 7);
      if (month !== this.dpPickerDate) {
        this.dpPickerDate = month;            // change the displayed month
        this.dpFocusDayWhenReady(next, 0);
      } else {
        this.dpFocusDayFromButtons(dayButtons, next);
      }
    }
  }
};
